<template>
    <q-dialog :model-value="modelValue" persistent @update:model-value="$emit('update:modelValue', $event)">
        <q-card class="tk-dialog-card" style="width: 900px; max-width: 96vw;">
            <q-form ref="sessionForm" @submit.prevent="submit">
                <q-card-section class="tk-dialog-header">
                    <h2>{{ session ? "Edit Group Session" : "Add Group Session" }}</h2>
                    <q-btn flat round dense icon="close" @click="$emit('update:modelValue', false)">
                        <q-tooltip class="text-body2">Close</q-tooltip>
                    </q-btn>
                </q-card-section>

                <q-card-section>
                    <div class="row q-col-gutter-md">
                        <div class="col-12 col-md-4 tk-field">
                            <p class="q-mb-xs"><strong>Session Date</strong></p>
                            <q-input
                                v-model="form.sessionDate"
                                outlined
                                dense
                                type="date"
                                lazy-rules="ondemand"
                                hide-bottom-space
                                :rules="[value => !!value || 'Session date is required.']"
                            />
                        </div>
                        <div class="col-12 col-md-4 tk-field">
                            <p class="q-mb-xs"><strong>Time Spent</strong></p>
                            <q-input
                                v-model.number="form.timeSpentMinutes"
                                outlined
                                dense
                                type="number"
                                min="0"
                                max="1440"
                                suffix="minutes"
                                lazy-rules="ondemand"
                                hide-bottom-space
                                :rules="[value => validMinutes(value) || 'Enter whole minutes from 0 to 1440.']"
                            />
                        </div>
                        <div class="col-12 tk-field">
                            <p class="q-mb-xs"><strong>Facilitators</strong></p>
                            <q-select
                                v-model="form.facilitatorIds"
                                :options="staffOptions"
                                outlined
                                dense
                                emit-value
                                map-options
                                multiple
                                use-chips
                                lazy-rules="ondemand"
                                hide-bottom-space
                                :rules="[value => value.length > 0 || 'Select at least one facilitator.']"
                            />
                        </div>
                        <div class="col-12 tk-field">
                            <p class="q-mb-xs"><strong>General Session Notes</strong></p>
                            <q-input
                                v-model="form.notes"
                                outlined
                                dense
                                type="textarea"
                                rows="8"
                                hint="Consider what was covered, session feedback, risks or incidents, and relevant follow-up actions."
                            />
                        </div>
                        <div class="col-12">
                            <q-banner rounded class="bg-blue-1 text-blue-10">
                                The total attendee count represents everyone who attended. The Attended list records which group members attended; unnamed attendees can still be included in the total.
                            </q-banner>
                        </div>
                        <div class="col-12 tk-field">
                            <p class="q-mb-xs"><strong>Group Member Attendance</strong></p>
                            <div class="text-caption text-grey-7 q-mb-md">
                                Everyone starts as Unattended. Move each person who attended into the Attended list.
                            </div>
                            <div class="row q-col-gutter-md">
                                <div class="col-12 col-md-6">
                                    <div class="text-subtitle2 q-mb-sm">Unattended</div>
                                    <q-list bordered separator>
                                        <q-item v-for="member in unattendedMembers" :key="member.value">
                                            <q-item-section>
                                                <q-item-label>{{ member.label }}</q-item-label>
                                                <q-item-label caption>{{ member.typeLabel }}</q-item-label>
                                            </q-item-section>
                                            <q-item-section side>
                                                <q-btn flat dense no-caps color="primary" icon-right="arrow_forward" label="Add" :aria-label="`Mark ${member.label} as attended`" @click="markAttended(member.value)" />
                                            </q-item-section>
                                        </q-item>
                                        <q-item v-if="!unattendedMembers.length">
                                            <q-item-section class="text-grey-7">No unattended members.</q-item-section>
                                        </q-item>
                                    </q-list>
                                </div>
                                <div class="col-12 col-md-6">
                                    <div class="text-subtitle2 q-mb-sm">Attended</div>
                                    <q-list bordered separator>
                                        <q-item v-for="member in attendedMembers" :key="member.value">
                                            <q-item-section side>
                                                <q-btn flat dense no-caps color="primary" icon="arrow_back" label="Remove" :aria-label="`Mark ${member.label} as unattended`" @click="markUnattended(member.value)" />
                                            </q-item-section>
                                            <q-item-section>
                                                <q-item-label>{{ member.label }}</q-item-label>
                                                <q-item-label caption>{{ member.typeLabel }}</q-item-label>
                                            </q-item-section>
                                        </q-item>
                                        <q-item v-if="!attendedMembers.length">
                                            <q-item-section class="text-grey-7">No members marked as attended.</q-item-section>
                                        </q-item>
                                    </q-list>
                                </div>
                            </div>
                        </div>
                    </div>
                </q-card-section>

                <q-card-actions align="right">
                    <q-btn flat no-caps color="negative" label="Cancel" @click="$emit('update:modelValue', false)" />
                    <q-btn unelevated no-caps color="primary" icon="save" label="Save Session" type="submit" :loading="saving" />
                </q-card-actions>
            </q-form>
        </q-card>
    </q-dialog>
</template>

<script>
const todayDate = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${now.getFullYear()}-${month}-${day}`;
};

const dateInputValue = (value) => String(value || "").slice(0, 10);

export default {
    name: "TkGroupSessionDialog",
    emits: ["update:modelValue", "save"],
    props: {
        modelValue: { type: Boolean, required: true },
        session: { type: Object, default: null },
        staffOptions: { type: Array, required: true },
        memberOptions: { type: Array, required: true },
        defaultFacilitatorIds: { type: Array, required: true },
        saving: { type: Boolean, required: true },
    },
    data() {
        return {
            form: {
                sessionDate: "",
                timeSpentMinutes: "",
                facilitatorIds: [],
                notes: "",
                totalAttendeeCount: 0,
                attendedMemberIds: [],
            },
        };
    },
    computed: {
        individuallyRecordedCount() {
            return this.form.attendedMemberIds.length;
        },
        selectableMembers() {
            const attendedIds = new Set(this.form.attendedMemberIds.map(value => String(value)));
            return this.memberOptions.filter(member => member.isActive || attendedIds.has(String(member.value)));
        },
        unattendedMembers() {
            const attendedIds = new Set(this.form.attendedMemberIds.map(value => String(value)));
            return this.selectableMembers.filter(member => !attendedIds.has(String(member.value)));
        },
        attendedMembers() {
            const attendedIds = new Set(this.form.attendedMemberIds.map(value => String(value)));
            return this.selectableMembers.filter(member => attendedIds.has(String(member.value)));
        },
    },
    watch: {
        modelValue(value) {
            if (value) {
                this.form = this.buildForm();
            }
        },
    },
    methods: {
        buildForm() {
            return {
                sessionDate: this.session ? dateInputValue(this.session.session_date) : todayDate(),
                timeSpentMinutes: this.session?.time_spent_minutes ?? "",
                facilitatorIds: this.session
                    ? this.session.facilitators.map(facilitator => facilitator.staff_id)
                    : [...this.defaultFacilitatorIds],
                notes: this.session?.notes ?? "",
                totalAttendeeCount: this.session?.attended_member_ids?.length ?? 0,
                attendedMemberIds: this.session ? [...this.session.attended_member_ids] : [],
            };
        },
        validMinutes(value) {
            const minutes = Number(value);
            return Number.isInteger(minutes) && minutes >= 0 && minutes <= 1440;
        },
        validAttendeeCount(value) {
            const count = Number(value);
            return Number.isInteger(count) && count >= 0;
        },
        markAttended(memberId) {
            if (this.form.attendedMemberIds.some(value => String(value) === String(memberId))) return;
            this.form.attendedMemberIds = [...this.form.attendedMemberIds, memberId];
        },
        markUnattended(memberId) {
            this.form.attendedMemberIds = this.form.attendedMemberIds
                .filter(value => String(value) !== String(memberId));
        },
        async submit() {
            if (!await this.$refs.sessionForm?.validate()) return;
            this.$emit("save", {
                session_date: this.form.sessionDate,
                time_spent_minutes: Number(this.form.timeSpentMinutes),
                facilitator_ids: [...this.form.facilitatorIds],
                notes: this.form.notes.trim() || null,
                total_attendee_count: this.form.attendedMemberIds?.length ?? 0,
                attended_member_ids: [...this.form.attendedMemberIds],
            });
        },
    },
};
</script>
